import useUppy from "@/features/createProject/uppyFactory/hooks/useUppy";
import { Outlet } from "react-router-dom";
import LoaderWrapper from "../LoaderWrapper";
import useConnectivityHook from "@/hooks/useConnectivityHook";
import { withAuthentication } from "@/lib/firebase";
import { useTypedSelector } from "@/config/configureAppStore";
import { STATUS } from "@/constants/status";
import SentryWrapper from "@/lib/sentry/SentryWrapper";
import React, { Suspense, useCallback, useEffect } from "react";
import Hotjar from "@hotjar/browser";
import { useAuth } from "@/lib/firebase/firebaseHooks";
import useCreditsPrompt from "@/hooks/useCreditsPrompt";
import useAnalytics from "@/hooks/useAnalytics";
import PricingDialog from "@/features/pricing/components/PricingDialog";
import usePageView from "@/hooks/usePageView";
import { redirectOnMobileDetect } from "@/utils/misc";
import useRedirectActions from "@/hooks/useRedirectActions";
import MurfSuiteDialog from "../MurfSuiteDialog";
import CreateProjectDialog from "@/features/createProject/routes/CreateProjectDialog";
import Calendly, {
  CALENDLY_WRAPPER_ID,
  StyledCalendlyWrapper
} from "@/components/elements/Calendly";
import { useDubNotifications } from "@/features/notifications/hooks/useDubNotifications";
import NotificationPermissionDialog from "@/features/notifications/components/NotificationPermissionDialog";

const CreditsPrompt = React.lazy(() =>
  import("@/features/credits").then((module) => ({
    default: module.CreditsPrompt
  }))
);

const UpgradePrompt = React.lazy(() =>
  import("@/features/credits").then((module) => ({
    default: module.UpgradePrompt
  }))
);

const LazyHubspotChat = React.lazy(
  () => import("@/components/elements/LazyHubspotChat")
);

const AppLayout = () => {
  const appInitializationStatus = useTypedSelector(
    (state) => state.globalState.appInitializationStatus
  );

  const {
    creditsDialog,
    upgradeDialog,
    murfSuite,
    createProjectDialog,
    calendlyDialog,
    notificationPermissionDialog
  } = useTypedSelector((state) => state.dialog);

  const auth = useAuth();

  useEffect(() => {
    redirectOnMobileDetect();
  }, []);

  useUppy();
  useConnectivityHook();
  useCreditsPrompt();
  useAnalytics();
  usePageView();
  useRedirectActions();
  useDubNotifications();

  const initializeHotjar = useCallback(() => {
    Hotjar.init(
      import.meta.env.VITE_MURF_HOTJAR_SITE_ID,
      import.meta.env.VITE_MURF_HOTJAR_VERSION
    );
  }, []);

  useEffect(() => {
    initializeHotjar();
    return () => {};
  }, [initializeHotjar]);

  useEffect(() => {
    if (auth) {
      const identifyHotjarUser = () => {
        Hotjar.identify(auth.uid, {
          display_name: auth.displayName,
          email: auth.email
        });
      };
      identifyHotjarUser();
    }
    return () => {};
  }, [auth]);

  return (
    <LoaderWrapper loading={appInitializationStatus !== STATUS.SUCCESS}>
      <SentryWrapper>
        <Outlet />
        {creditsDialog.open && (
          <Suspense fallback={<div />}>
            <CreditsPrompt />
          </Suspense>
        )}
        {upgradeDialog.open && (
          <Suspense fallback={<div />}>
            <UpgradePrompt />
          </Suspense>
        )}
        {murfSuite.open ? (
          <Suspense fallback={<div />}>
            <MurfSuiteDialog />
          </Suspense>
        ) : null}
        {createProjectDialog.open ? (
          <Suspense fallback={<div />}>
            <CreateProjectDialog />
          </Suspense>
        ) : null}
        <Suspense fallback={<div />}>
          <LazyHubspotChat />
        </Suspense>
        <StyledCalendlyWrapper id={CALENDLY_WRAPPER_ID}>
          {calendlyDialog.open ? (
            <Suspense fallback={<div />}>
              <Calendly />
            </Suspense>
          ) : null}
        </StyledCalendlyWrapper>
        {notificationPermissionDialog.open ? (
          <Suspense fallback={<div />}>
            <NotificationPermissionDialog />
          </Suspense>
        ) : null}
        <PricingDialog />
      </SentryWrapper>
    </LoaderWrapper>
  );
};

const AppLayoutAuth = withAuthentication(AppLayout);

export default AppLayoutAuth;
